import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi, adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const FILE_EMOJI = { xlsx:'📊', xls:'📊', xlsm:'📊', csv:'📋', pdf:'📄' };

// ── Folder Map Modal ──────────────────────────────────────────────────────────
function FolderModal({ report, onClose, onSave }) {
  const [form, setForm] = useState({
    folderId:       report.mappedFolderId    || '',
    folderPath:     report.mappedFolderPath  || '',
    folderName:     report.mappedFolderName  || '',
    reportFileName: report.reportFileName    || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.folderId.trim()) { toast.error('Folder ID is required'); return; }
    setSaving(true);
    try {
      const res = await reportsApi.mapFolder(report.id, {
        folderId:       form.folderId.trim(),
        folderPath:     form.folderPath.trim()     || null,
        folderName:     form.folderName.trim()     || null,
        reportFileName: form.reportFileName.trim() || null,
      });
      toast.success(res.data.message);
      onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save mapping');
    } finally { setSaving(false); }
  };

  const handleUnmap = async () => {
    if (!window.confirm(`Remove all folder mapping from "${report.name}"?`)) return;
    setSaving(true);
    try {
      await reportsApi.unmapFolder(report.id);
      toast.success('Folder mapping removed');
      onSave(); onClose();
    } catch { toast.error('Failed to unmap'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Map OneDrive folder</div>
        <div className="modal-sub">
          Configure where <strong>{report.name}</strong> ({report.fileType?.toUpperCase()}) lives in OneDrive
        </div>

        {/* Graph Explorer hint */}
        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          💡 <strong>Find a Folder ID:</strong> open{' '}
          <code style={{ fontSize: 11, background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>
            developer.microsoft.com/graph/graph-explorer
          </code>
          {' '}→ sign in → run{' '}
          <code style={{ fontSize: 11, background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>
            GET /me/drive/root:/Reports/FolderName
          </code>
          {' '}→ copy the <code style={{ fontSize: 11 }}>"id"</code> value.
        </div>

        <form onSubmit={handleSave}>
          {/* Folder ID */}
          <div className="form-field">
            <label className="form-label">OneDrive Folder ID *</label>
            <input className="form-input" value={form.folderId} onChange={e => set('folderId', e.target.value)}
              placeholder="01ABCDEF123456789ABCDEF" required
              style={{ fontFamily: 'monospace', fontSize: 13 }} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
              The internal Microsoft item ID of the folder
            </div>
          </div>

          {/* Report File Name — optional */}
          <div className="form-field">
            <label className="form-label">
              Report file name
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, fontSize: 11 }}>optional</span>
            </label>
            <input className="form-input" value={form.reportFileName} onChange={e => set('reportFileName', e.target.value)}
              placeholder="e.g. Crucial_Report_June.xlsx  or  Q2_Summary.pdf"
              style={{ fontFamily: 'monospace', fontSize: 13 }} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>
              If set, PX-Reports opens <strong>this exact file</strong> from the folder.
              If left blank, the <strong>most recently modified</strong> file in the folder is used.
            </div>
          </div>

          {/* Folder path and display name — side by side */}
          <div className="folder-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label className="form-label">Folder path <span style={{ fontWeight:400, color:'var(--text-muted)', fontSize:11 }}>display</span></label>
              <input className="form-input" value={form.folderPath} onChange={e => set('folderPath', e.target.value)}
                placeholder="/Reports/Daily/Crucial" />
            </div>
            <div className="form-field">
              <label className="form-label">Display name <span style={{ fontWeight:400, color:'var(--text-muted)', fontSize:11 }}>display</span></label>
              <input className="form-input" value={form.folderName} onChange={e => set('folderName', e.target.value)}
                placeholder="e.g. Crucial Reports" />
            </div>
          </div>

          <div className="modal-actions">
            {report.mappedFolderId && (
              <button type="button" className="btn btn-danger" onClick={handleUnmap} disabled={saving}>
                Remove mapping
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save mapping'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FolderMappingPage() {
  const [reports, setReports]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all'); // all | mapped | unmapped
  const [editing, setEditing]     = useState(null);
  const [syncing, setSyncing]     = useState(false);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.list({ limit: 200 });
      setReports(res.data.reports);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await adminApi.triggerSync();
      toast.success(res.data.message);
      await loadReports();
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const visible = reports.filter(r => {
    const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
      (r.mappedFolderName || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.reportFileName || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all'
      || (filter === 'mapped'   ?  !!r.mappedFolderId : !r.mappedFolderId);
    return matchSearch && matchFilter;
  });

  const mapped   = reports.filter(r => r.mappedFolderId).length;
  const unmapped = reports.filter(r => !r.mappedFolderId).length;
  const hasFile  = reports.filter(r => r.reportFileName).length;

  return (
    <>
      {editing && (
        <FolderModal report={editing} onClose={() => setEditing(null)} onSave={loadReports} />
      )}

      <div className="page-header">
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', paddingBottom:16, flexWrap:'wrap', gap:10 }}>
          <div>
            <div className="page-title">Folder Mapping</div>
            <div className="page-subtitle">Map each report to its OneDrive folder and optionally pin a specific filename</div>
          </div>
          <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            {syncing ? 'Syncing…' : 'Sync from OneDrive'}
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:12, marginBottom:20 }}>
          {[
            { label:'Total reports',  val: reports.length, color: 'var(--accent)'      },
            { label:'Folder mapped',  val: mapped,         color: 'var(--success)'     },
            { label:'File pinned',    val: hasFile,        color: '#7C3AED'            },
            { label:'Unmapped',       val: unmapped,       color: unmapped > 0 ? 'var(--warning)' : 'var(--text-muted)' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ padding:'14px 16px' }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-val" style={{ fontSize:22, color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div className="search-box" style={{ flex:1, minWidth:200 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input placeholder="Search by name, folder, or filename…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
            {[['all','All'], ['mapped','✓ Mapped'], ['unmapped','⚠ Unmapped']].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)}
                style={{ padding:'7px 14px', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'var(--font)',
                  background: filter === v ? 'var(--accent)' : 'white',
                  color: filter === v ? 'white' : 'var(--text-secondary)', transition:'all 0.15s' }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}><div className="spinner"/></div>
        ) : (
          <div className="card">
            <div style={{ overflowX:'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Folder</th>
                    <th>File name</th>
                    <th>Modified</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:18, flexShrink:0 }}>{FILE_EMOJI[r.fileType?.toLowerCase()] || '📁'}</span>
                          <span style={{ fontWeight:600, fontSize:13 }}>{r.name}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, fontWeight:700,
                          background: r.fileType === 'pdf' ? '#FEF2F2' : '#F0FDF4',
                          color:      r.fileType === 'pdf' ? '#DC2626' : '#15803D' }}>
                          {r.fileType?.toUpperCase()}
                        </span>
                      </td>
                      <td><span className={`category-badge cat-${r.category}`}>{r.category}</span></td>

                      {/* Folder column */}
                      <td>
                        {r.mappedFolderId ? (
                          <div>
                            <div style={{ fontWeight:600, fontSize:12, color:'var(--success)', display:'flex', alignItems:'center', gap:5 }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                              {r.mappedFolderName || 'Mapped'}
                            </div>
                            {r.mappedFolderPath && (
                              <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace', marginTop:2 }}>
                                {r.mappedFolderPath}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize:12, color:'var(--text-muted)', fontStyle:'italic' }}>— Not mapped</span>
                        )}
                      </td>

                      {/* File name column */}
                      <td>
                        {r.reportFileName ? (
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontSize:12, fontFamily:'monospace', color:'#7C3AED',
                              background:'#F5F3FF', padding:'2px 8px', borderRadius:6,
                              maxWidth:160, overflow:'hidden', textOverflow:'ellipsis',
                              whiteSpace:'nowrap', display:'inline-block' }}
                              title={r.reportFileName}>
                              📎 {r.reportFileName}
                            </span>
                          </div>
                        ) : (
                          r.mappedFolderId
                            ? <span style={{ fontSize:11, color:'var(--text-muted)', fontStyle:'italic' }}>Latest file in folder</span>
                            : <span style={{ fontSize:11, color:'var(--text-muted)' }}>—</span>
                        )}
                      </td>

                      <td style={{ fontSize:12, color:'var(--text-muted)' }}>
                        {r.lastModified ? formatDistanceToNow(new Date(r.lastModified), { addSuffix:true }) : '—'}
                      </td>
                      <td>
                        <button className="btn btn-secondary" style={{ padding:'5px 12px', fontSize:12 }}
                          onClick={() => setEditing(r)}>
                          {r.mappedFolderId ? 'Edit' : 'Map folder'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visible.length === 0 && !loading && (
              <div className="empty-state"><p>No reports match your filter.</p></div>
            )}
          </div>
        )}

        {/* How it works */}
        <div style={{ marginTop:20, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, padding:'16px 20px', fontSize:13 }}>
          <div style={{ fontWeight:700, marginBottom:10, fontSize:14 }}>📖 How folder mapping works</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:12 }}>
            <div style={{ background:'#F5F3FF', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontWeight:700, color:'#7C3AED', fontSize:12, marginBottom:4 }}>📎 File name set</div>
              <div style={{ color:'var(--text-secondary)', fontSize:12, lineHeight:1.6 }}>
                Opens that <strong>exact file</strong> from the mapped folder every time. Best when the filename never changes (e.g. <code style={{ fontSize:11 }}>Crucial_Report.xlsx</code>).
              </div>
            </div>
            <div style={{ background:'#F0FDF4', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontWeight:700, color:'var(--success)', fontSize:12, marginBottom:4 }}>📁 Folder only (no filename)</div>
              <div style={{ color:'var(--text-secondary)', fontSize:12, lineHeight:1.6 }}>
                Automatically opens the <strong>most recently modified</strong> file in the folder. Best for reports that get re-uploaded with new names each month.
              </div>
            </div>
            <div style={{ background:'#FFF7ED', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontWeight:700, color:'var(--accent)', fontSize:12, marginBottom:4 }}>🔄 Cache</div>
              <div style={{ color:'var(--text-secondary)', fontSize:12, lineHeight:1.6 }}>
                Parsed data is cached for <strong>30 minutes</strong>. Click <strong>Refresh</strong> in the report viewer to force a re-read from OneDrive immediately.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
